const WIDGET_SELECTOR = '.g-recaptcha';
const CONTAINER_SELECTOR = '.form__captcha';
const ONLOAD_CALLBACK = 'onRecaptchaV2ApiLoad';
const SCRIPT_URL = 'https://www.google.com/recaptcha/api.js';

type WidgetSize = 'normal' | 'compact' | 'invisible';

interface RenderOptions {
    sitekey: string;
    size: WidgetSize;
    theme: string;
    callback: (token: string) => void;
    'expired-callback': () => void;
    'error-callback': () => void;
}

interface RecaptchaApi {
    render: (element: HTMLElement, options: RenderOptions) => number;
    getResponse: (widgetId?: number) => string;
    execute: (widgetId?: number) => void;
}

interface WidgetConfig {
    siteKey: string;
    size: WidgetSize;
    theme: string;
    language: string;
}

declare global {
    interface Window {
        grecaptcha?: RecaptchaApi;
        onRecaptchaV2ApiLoad?: () => void;
    }
}

let apiPromise: Promise<RecaptchaApi> | null = null;

function loadApi(language: string): Promise<RecaptchaApi> {
    if (window.grecaptcha?.render) {
        return Promise.resolve(window.grecaptcha);
    }

    if (apiPromise !== null) return apiPromise;

    apiPromise = new Promise<RecaptchaApi>((resolve, reject) => {
        window.onRecaptchaV2ApiLoad = () => {
            resolve(window.grecaptcha as RecaptchaApi);
        };

        const script = document.createElement('script');
        script.src = `${SCRIPT_URL}?render=explicit&onload=${ONLOAD_CALLBACK}&hl=${encodeURIComponent(language)}`;
        script.async = true;
        script.defer = true;
        script.addEventListener('error', () => reject(new Error('Failed to load reCAPTCHA')));
        document.head.appendChild(script);
    });

    return apiPromise;
}

function readWidgetConfig(widget: HTMLElement): WidgetConfig | null {
    const siteKey = widget.dataset.sitekey?.trim() ?? '';
    if (siteKey === '') return null;

    return {
        siteKey,
        size: (widget.dataset.size?.trim() || 'normal') as WidgetSize,
        theme: widget.dataset.theme?.trim() || 'light',
        language: widget.dataset.hl?.trim() || 'de',
    };
}

function findHiddenInput(widget: HTMLElement): HTMLInputElement | null {
    const container = widget.closest(CONTAINER_SELECTOR) ?? widget.parentElement;
    return container?.querySelector<HTMLInputElement>('input[type="hidden"]') ?? null;
}

function bindWidget(api: RecaptchaApi, widget: HTMLElement): void {
    const config = readWidgetConfig(widget);
    if (config === null) return;

    const input = findHiddenInput(widget);
    const form = widget.closest('form');
    if (input === null || form === null) return;

    // The token is single-use and must never be restored from a previous render.
    input.value = '';

    let executing = false;

    const widgetId = api.render(widget, {
        sitekey: config.siteKey,
        size: config.size,
        theme: config.theme,
        callback: (token: string) => {
            input.value = token;

            // The invisible widget only produces a token after execute(), so the pending
            // submission has to be triggered again once the callback has fired.
            if (config.size === 'invisible') {
                if (typeof form.requestSubmit === 'function') {
                    form.requestSubmit();
                } else {
                    form.submit();
                }
            }
        },
        'expired-callback': () => {
            input.value = '';
            executing = false;
        },
        'error-callback': () => {
            input.value = '';
            executing = false;
        },
    });

    form.addEventListener('submit', (event) => {
        if (config.size === 'invisible') {
            // Let the second submit pass once execute() has delivered its token.
            if (executing) return;

            executing = true;
            event.preventDefault();
            api.execute(widgetId);
            return;
        }

        input.value = api.getResponse(widgetId);
        if (input.value !== '') return;

        // The checkbox widget is visible, so guiding the user to it is enough.
        event.preventDefault();
        widget.scrollIntoView({ behavior: 'smooth', block: 'center' });
    });
}

export function initRecaptchaV2(): void {
    const widgets = document.querySelectorAll<HTMLElement>(WIDGET_SELECTOR);
    if (widgets.length === 0) return;

    const language = widgets[0].dataset.hl?.trim() || 'de';

    loadApi(language)
        .then((api) => {
            widgets.forEach((widget) => bindWidget(api, widget));
        })
        .catch(() => {
            // Google's script can be blocked by a content blocker. The widget then stays
            // unrendered and the server-side validation reports the missing token.
        });
}
